import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  ALL_VALUE,
  CenterSpinner,
  DateFilterField,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  RowKV,
  SearchBox,
  SelectSheet,
  SummaryCard,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { formatVnd } from "../../../utils/financeFormat";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../../utils/useFilterOptions";
import { getBdcManagerEmployeesApi } from "../../../api/bdcManager.api";
import { EMP_BASE_PATH } from "../../../config";

const LIMIT = 10;

const CCMG_STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: 1, label: "Có CCMG" },
  { key: 0, label: "Không có CCMG" },
];
const DEPARTMENT_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: 1, label: "Kinh doanh" },
  { key: 0, label: "Hỗ trợ kinh doanh" },
];
const SORT_OPTIONS = [
  { key: "desc", label: "Mới đến cũ" },
  { key: "asc", label: "Cũ đến mới" },
];

const EmployeeCard = ({ item, onClick }) => (
  <Box
    as="button"
    type="button"
    w="100%"
    textAlign="left"
    onClick={onClick}
    bg="white"
    border="1px solid"
    borderColor={M.border}
    borderRadius="12px"
    p="14px"
  >
    <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
      {item?.fullName || ""}
      {item?.code ? ` - ${item.code}` : ""}
    </Text>
    <Box h="1px" bg={M.border} mt="12px" mb="12px" />
    <Flex direction="column" gap="8px">
      <RowKV
        label="Trạng thái CCMG"
        value={item?.isBrokerDebtCertificate ? "Có CCMG" : "Không có CCMG"}
        valueColor={item?.isBrokerDebtCertificate ? M.green : M.gray}
        labelFlex="0.5"
      />
      <RowKV label="Số tiền tạm giữ" value={formatVnd(item?.amountHeld || 0)} valueColor={M.primary} labelFlex="0.5" />
      <RowKV label="Số tiền còn lại" value={formatVnd(item?.amountActualRemaining || 0)} labelFlex="0.5" />
      <RowKV
        label="Thuế TNCN"
        value={formatVnd(
          item?.taxTNCN != null ? item.taxTNCN : Math.round(0.1 * (item?.amountActualRemaining || 0))
        )}
        labelFlex="0.5"
      />
      <RowKV label="Số tiền đã trả" value={formatVnd(item?.amountPaid || 0)} labelFlex="0.5" />
    </Flex>
  </Box>
);

const CcmgEmployeeList = ({ mode }) => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isHeld = mode === "held";
  const title = isHeld ? "Tạm giữ CCMG" : "Lịch sử thanh toán CCMG";
  const detailPath = isHeld ? "tam-giu" : "thanh-toan";

  const [keyword, setKeyword] = useState("");
  const [filterOpen, setFilterOpen] = useState(true);
  const [ccmgStatus, setCcmgStatus] = useState(ALL_VALUE);
  const [department, setDepartment] = useState(ALL_VALUE);
  const [supportDept, setSupportDept] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [area, setArea] = useState(ALL_OPTION);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const buildParams = useCallback(
    (p) => {
      const params = { page: p, limit: LIMIT, sortOrder, search: keyword || undefined };
      if (ccmgStatus !== ALL_VALUE) params.ccmgStatus = Number(ccmgStatus);
      if (department !== ALL_VALUE) params.department = Number(department);
      if (validId(supportDept)) params.supportDepartmentId = validId(supportDept);
      if (validId(block)) params.saleBlockId = validId(block);
      if (validId(area)) params.saleAreaId = validId(area);
      if (fromDate) params.startTime = moment(fromDate).format("YYYY-MM-DD");
      if (toDate) params.endTime = moment(toDate).format("YYYY-MM-DD");
      return params;
    },
    [sortOrder, keyword, ccmgStatus, department, supportDept, block, area, fromDate, toDate]
  );

  const fetchPage = useCallback(
    async (p, listMode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getBdcManagerEmployeesApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (listMode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        setPage(p);
      } catch {
        if (rid === reqId.current && listMode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, ccmgStatus, department, supportDept?._id, block?._id, area?._id, fromDate, toDate, sortOrder]);

  const statusLabel = CCMG_STATUS_OPTIONS.find((s) => s.key === ccmgStatus)?.label ?? "Tất cả";
  const sortLabel = SORT_OPTIONS.find((s) => s.key === sortOrder)?.label ?? "Mới đến cũ";

  const sheetTitle =
    {
      CCMG_STATUS: "Trạng thái CCMG",
      DEPARTMENT: "Phòng ban",
      SUPPORT_DEPARTMENT: "Phòng ban",
      BLOCK: "Khối",
      AREA: "Vùng",
      SORT: "Sắp xếp",
    }[sheet.type] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "CCMG_STATUS") return CCMG_STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "DEPARTMENT") return DEPARTMENT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SUPPORT_DEPARTMENT") return buildOptionSheetData(options.items, supportDept);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    return [];
  }, [sheet.type, options.items, supportDept, block, area]);

  const selectedKey = {
    CCMG_STATUS: ccmgStatus,
    DEPARTMENT: department,
    SORT: sortOrder,
    SUPPORT_DEPARTMENT: supportDept?._id,
    BLOCK: block?._id,
    AREA: area?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "CCMG_STATUS") setCcmgStatus(raw);
    if (sheet.type === "DEPARTMENT") setDepartment(raw);
    if (sheet.type === "SORT") setSortOrder(raw);
    if (sheet.type === "SUPPORT_DEPARTMENT") setSupportDept(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "AREA") setArea(raw);
    sheet.close();
    setSheetSearch("");
  };

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["SUPPORT_DEPARTMENT", "BLOCK", "AREA"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => ({
      id: it._id,
      raw: it,
      employee: `${it?.fullName || ""}${it?.code ? ` - ${it.code}` : ""}` || "-",
      hasCcmg: !!it?.isBrokerDebtCertificate,
      ccmg: it?.isBrokerDebtCertificate ? "Có CCMG" : "Không có CCMG",
      held: formatVnd(it?.amountHeld || 0),
      remaining: formatVnd(it?.amountActualRemaining || 0),
      tax: formatVnd(
        it?.taxTNCN != null ? it.taxTNCN : Math.round(0.1 * (it?.amountActualRemaining || 0)),
      ),
      paid: formatVnd(it?.amountPaid || 0),
    }));

    return (
      <DesktopPage title={title}>
        <DesktopStatRow items={[{ key: "total", title: "Số nhân viên", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="320px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Tìm kiếm
              </Text>
              <DesktopSearch
                placeholder="Nhập tên nhân viên ..."
                onChange={(e) => setKeyword(e.target.value)}
                maxW="320px"
              />
            </Box>
            <FilterField
              label="Trạng thái"
              value={statusLabel}
              onClick={() => sheet.open("CCMG_STATUS")}
              width="200px"
            />
            <FilterField
              label="Phòng ban"
              value={supportDept?.name || "Tất cả"}
              onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
              width="200px"
            />
            <FilterField
              label="Khối"
              value={block?.name || "Tất cả"}
              onClick={() => sheet.open("BLOCK")}
              width="200px"
            />
            <FilterField
              label="Vùng"
              value={area?.name || "Tất cả"}
              onClick={() => sheet.open("AREA")}
              width="200px"
            />
            <DateFilterField
              label="Từ ngày"
              value={fromDate}
              onChange={setFromDate}
              placeholder="Tất cả"
              width="180px"
            />
            <DateFilterField
              label="Đến ngày"
              value={toDate}
              onChange={setToDate}
              placeholder="Tất cả"
              width="180px"
            />
            <Box w="180px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortLabel}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            onRowClick={(row) =>
              navigate(
                `${EMP_BASE_PATH}/quan-ly/ccmg/${detailPath}/${row.id}?name=${encodeURIComponent(
                  row.raw.fullName || "",
                )}&code=${encodeURIComponent(row.raw.code || "")}`,
              )
            }
            rows={rows}
            headers={[
              { key: "employee", label: "Nhân viên", align: "left", width: "26%" },
              {
                key: "ccmg",
                label: "Trạng thái CCMG",
                width: "16%",
                render: (row) => (
                  <Text fontSize="14px" color={row.hasCcmg ? M.green : M.gray}>
                    {row.ccmg}
                  </Text>
                ),
              },
              {
                key: "held",
                label: "Số tiền tạm giữ",
                align: "right",
                width: "15%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={M.primary}>
                    {row.held}
                  </Text>
                ),
              },
              { key: "remaining", label: "Số tiền còn lại", align: "right", width: "15%" },
              { key: "tax", label: "Thuế TNCN", align: "right", width: "13%" },
              { key: "paid", label: "Số tiền đã trả", align: "right", width: "15%" },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={title} />

      <Box px="16px" pt="12px">
        <SearchBox onChange={setKeyword} />

        <Box h="12px" />

        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          <FilterField label="Trạng thái" value={statusLabel} onClick={() => sheet.open("CCMG_STATUS")} />
          <FilterField
            label="Phòng ban"
            value={supportDept?.name || "Tất cả"}
            onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
          />
          <FilterField label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />
          <FilterField label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />
          <DateFilterField label="Từ ngày" value={fromDate} onChange={setFromDate} placeholder="Tất cả" />
          <DateFilterField label="Đến ngày" value={toDate} onChange={setToDate} placeholder="Tất cả" />
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text="SỐ NHÂN VIÊN" total={total} />

        <ListHeaderRow title="Danh sách nhân viên" sortLabel={sortLabel} onSort={() => sheet.open("SORT")} />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <EmployeeCard
                key={it._id}
                item={it}
                onClick={() =>
                  navigate(
                    `${EMP_BASE_PATH}/quan-ly/ccmg/${detailPath}/${it._id}?name=${encodeURIComponent(
                      it.fullName || ""
                    )}&code=${encodeURIComponent(it.code || "")}`
                  )
                }
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default CcmgEmployeeList;
